
import { defineComponent, computed, onMounted, ref, Ref } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import useSilentLogin from '@/composables/useSilentLogin';
import useNavigateTo from '@/composables/useNavigateTo';
import useHandleKey from '@/composables/useHandleKey';
import { Credentials } from '@/interfaces/credentials.interface';
import { CxServiceUnavailableError } from '@/classes/error.class';
import CxSpinner from '@/components/CxSpinner.vue';
import loginApi from '@/http/login.api';
import { ErrorType, useDisplayError } from '@/composables/useDisplayError';

export default defineComponent({
  name: 'CxLogin',
  components: { CxSpinner },
  setup() {
    // Use composables and plugins
    const { isSilentLoginInProgress, isSilentRedirectInProgress, redirectToConX, silentLogin } = useSilentLogin();
    const { navigateToRecovery } = useNavigateTo();
    const { handleKeyDown } = useHandleKey();
    const language = useGettext();
    const { currentErrorType, errorText } = useDisplayError(language);

    // Data
    const username: Ref<string> = ref('');
    const password: Ref<string> = ref('');
    const rememberMe: Ref<boolean> = ref(true);
    const isLoginQueryInProgress: Ref<boolean> = ref(false);

    // Computed
    const translations = computed(
      (): {
        welcome: string;
        username: string;
        password: string;
        remember: string;
        login: string;
        reset: string;
      } => {
        return {
          welcome: language.$gettext('Welcome to Leica ConX'),
          username: language.$gettext('Username'),
          password: language.$gettext('Password'),
          remember: language.$gettext('Remember me'),
          login: language.$gettext('Login'),
          reset: language.$gettext('Trouble logging in?')
        };
      }
    );
    const canLogIn = computed((): boolean => {
      return !!username.value && !!password.value;
    });

    // Methods
    const onSilentLoginError = () => {
      currentErrorType.value = ErrorType.ServiceUnavailable;
    };
    const logIn = async (): Promise<void> => {
      console.log('test!!!!!');
      try {
        isLoginQueryInProgress.value = true;
        const credentials: Credentials = {
          username: username.value,
          password: password.value,
          rememberMe: rememberMe.value
        };
        const result = await loginApi.logIn(credentials);
        if (!result) currentErrorType.value = ErrorType.InvalidCredentials;
        // on success we redirect to the ConX main page
        else redirectToConX();
      } catch (error) {
        console.log('error', error);
        if (error instanceof CxServiceUnavailableError) currentErrorType.value = ErrorType.ServiceUnavailable;
        else currentErrorType.value = ErrorType.UnexpectedError;
      } finally {
        isLoginQueryInProgress.value = false;
      }
    };

    // Lifecycle Hooks
    onMounted(() => silentLogin(onSilentLoginError));

    // Expose properties and methods
    return {
      // Data
      username,
      password,
      rememberMe,
      isLoginQueryInProgress,
      isSilentLoginInProgress,
      isSilentRedirectInProgress,
      currentErrorType,
      // Computed
      translations,
      canLogIn,
      errorText,
      // Methods
      logIn,
      navigateToRecovery,
      handleKeyDown
    };
  }
});
